import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { ScrollView, View } from 'react-native';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import TimelineItem from './TimelineItem';
import styles from './ItineraryEditor.styles';
import NoteEditor from './NoteEditor';
import { addNoteEntry } from '../../shared/itineraryApi';
import { useGlobalToast } from '../../../../../apps/web/src/contexts/ToastContext';
import { ProfileModal } from '@cubemeet/ui';
import { compareTimelineItems } from '../timeUtils';

// 简单的Tooltip组件
const Tooltip = ({ text, children }: { text: string; children: React.ReactNode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPinned, setIsPinned] = useState(false);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        onClick={(e) => {
          e.stopPropagation();
          // 点击切换固定状态
          setIsPinned(!isPinned);
          setIsVisible(!isPinned);
        }}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => { if (!isPinned) setIsVisible(false); }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {children}
      </div>
      
      {isVisible && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(calc(-50% - 4px))',
            top: 'calc(100% + 8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '10px 14px',
            borderRadius: '10px',
            fontSize: '13px',
            whiteSpace: 'nowrap',
            zIndex: 120000,
            boxShadow: '0 6px 22px rgba(0,0,0,0.42)',
            fontWeight: '500',
            pointerEvents: 'none'
          }}
        >
          {text}
          {/* small triangular pointer pointing up from the tooltip to the handle, centered */}
          <div style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(calc(-50% - 4px))',
            top: -8,
            width: 0,
            height: 0,
            borderLeft: '8px solid transparent',
            borderRight: '8px solid transparent',
            borderBottom: '8px solid rgba(0, 0, 0, 0.9)',
            pointerEvents: 'none'
          }} />
        </div>
      )}
    </div>
  );
};

// 多卡片輪播組件 - 寬屏用箭頭切換，窄屏用滑動
const MultiCardCarousel: React.FC<{
  group: { time: string; items: any[] };
  groupIndex: number;
  groupedByTime: { time: string; items: any[] }[];
  displayedItems: any[];
  items: any[];
  isNarrow: boolean;
  isEditing: boolean;
  editor: any;
  selectedDay: any;
  expandedCards: Set<string>;
  toggleExpand: (id: string) => void;
  setOpenPoi: (item: any, itemIndex: number, context?: { fromPlaceholder?: boolean; payloadOverride?: any }) => void;
  setInlineEditTarget?: (t: any) => void;
  inlineEditTarget?: { dayIndex: number; itemIndex: number; mode?: string } | null;
  parseTimeToMinutes: (t?: string) => number | undefined;
  getNowMinutes: () => number;
  supabaseClient?: any;
  currentUserId?: string | null;
  canEdit?: boolean;
  groupIdForLocal?: string | null;
  memberProfiles?: Record<string, { display_name?: string | null; avatar_url?: string | null }>;
  onSelectFromCardPicker?: (dayIndex: number, itemIndex: number) => void;
  onImageClick?: (images: any[], startIndex: number) => void;
  setOpenInsertIndex: (index: number | null) => void;
  openInsertIndex: number | null;
  insertSaving: boolean;
  onSaveNote: (index: number, data: any) => Promise<any>;
  onCancelNote: () => void;
  onGalleryUpdateFactory: (index: number) => ((gallery: any[], entryId?: string) => Promise<void>) | undefined;
}> = ({
  group,
  groupIndex,
  groupedByTime,
  displayedItems,
  items,
  isNarrow,
  isEditing,
  editor,
  selectedDay,
  expandedCards,
  toggleExpand,
  setOpenPoi,
  setInlineEditTarget,
  inlineEditTarget,
  parseTimeToMinutes,
  getNowMinutes,
  supabaseClient,
  currentUserId,
  canEdit,
  groupIdForLocal,
  memberProfiles,
  onSelectFromCardPicker,
  onImageClick,
  setOpenInsertIndex,
  openInsertIndex,
  insertSaving,
  onSaveNote,
  onCancelNote,
  onGalleryUpdateFactory,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  const totalItems = group.items.length;
  
  // 移除複雜的滾動監聽和高度計算，改用 CSS Flexbox 自動佈局
  // 這能避免在滑動時觸發重渲染導致的頁面跳動和點擊失效問題
  
  const goToPrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };
  
  const goToNext = () => {
    setCurrentIndex((prev) => (prev < totalItems - 1 ? prev + 1 : prev));
  };

  // 計算當前顯示的項目
  const currentItem = group.items[currentIndex];
  let globalIndex = 0;
  for (let i = 0; i < groupIndex; i++) {
    globalIndex += groupedByTime[i].items.length;
  }
  globalIndex += currentIndex;
  const index = globalIndex;
  const arr = displayedItems;
  const sourceIndex = items.indexOf(currentItem);

  // 窄屏：水平滾動模式（僅在非編輯模式下使用）
  if (isNarrow && !isEditing) {
    // 計算當前組的索引範圍
    let groupStartIdx = 0;
    for (let i = 0; i < groupIndex; i++) {
      groupStartIdx += groupedByTime[i].items.length;
    }
    const groupEndIdx = groupStartIdx + group.items.length - 1;
    // 判斷 openInsertIndex 是否屬於當前組
    const isOpenInThisGroup = openInsertIndex !== null && openInsertIndex >= groupStartIdx && openInsertIndex <= groupEndIdx;

    // 計算時間軸進度（與普通卡片一致）
    const nowMinutes = typeof getNowMinutes === 'function' ? getNowMinutes() : (new Date()).getHours() * 60 + (new Date()).getMinutes();
    const thisMinutes = typeof parseTimeToMinutes === 'function' ? parseTimeToMinutes(group.time) ?? 0 : 0;
    // 找下一個時間組
    const nextGroup = groupIndex < groupedByTime.length - 1 ? groupedByTime[groupIndex + 1] : null;
    const nextMinutes = nextGroup && typeof parseTimeToMinutes === 'function' ? parseTimeToMinutes(nextGroup.time) : undefined;
    let connectorProgress = 0;
    if (typeof nextMinutes === 'number' && nextMinutes > thisMinutes) {
      connectorProgress = (nowMinutes - thisMinutes) / (nextMinutes - thisMinutes);
    } else {
      connectorProgress = nowMinutes >= thisMinutes ? 1 : 0;
    }
    // clamp 0-1
    if (!isFinite(connectorProgress)) connectorProgress = 0;
    if (connectorProgress < 0) connectorProgress = 0;
    if (connectorProgress > 1) connectorProgress = 1;

    return (
      <>
        <div 
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            marginBottom: '8px',
            position: 'relative',
          }}
        >
        {/* 固定時間列：時間 pill + dot + 進度豎線 */}
        <div data-timeline-left="1" style={{
          width: 60,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 16,
          position: 'relative'
        }}>
          <div style={{
            backgroundColor: '#F97316',
            paddingLeft: 10,
            paddingRight: 10,
            paddingTop: 6,
            paddingBottom: 6,
            borderRadius: 16,
          }}>
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 12 }}>
              {group.time || '09:00'}
            </span>
          </div>
          {/* dot (顯示於 pill 下方) */}
          <div data-timeline-dot="1" style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            backgroundColor: '#FB7A4A',
            marginTop: 6,
            zIndex: 2,
          }} />
          {/* 進度豎線 - 根據當前時間動態顯示 */}
          <div id="timeline-left-line" style={{
            width: 3,
            flex: 1,
            minHeight: 56,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 1.5,
            zIndex: 1,
            marginTop: 0,
            marginBottom: 87,
            overflow: 'hidden',
          }}>
            {/* 已完成部分（橙色） */}
            <div style={{
              width: 3,
              flex: connectorProgress,
              backgroundColor: '#FB7A4A',
              borderRadius: connectorProgress >= 1 ? 1.5 : '1.5px 1.5px 0 0',
            }} />
            {/* 未完成部分（灰色） */}
            <div style={{
              width: 3,
              flex: 1 - connectorProgress,
              backgroundColor: '#D1D5DB',
              borderRadius: connectorProgress <= 0 ? 1.5 : '0 0 1.5px 1.5px',
            }} />
          </div>
          {/* debug overlay if activated via URL param ?timelineDebug=1 */}
          {typeof window !== 'undefined' && window.location && window.location.search && window.location.search.indexOf('timelineDebug=1') !== -1 && (
            <div style={{
              position: 'absolute',
              left: 70,
              top: 8,
              background: 'rgba(0,0,0,0.6)',
              color: '#fff',
              padding: '6px 8px',
              fontSize: 12,
              borderRadius: 6,
              zIndex: 99999,
              pointerEvents: 'none'
            }} id="timeline-debug-overlay">measuring...</div>
          )}
        </div>
        {/* 水平滾動卡片容器 */}
        <div 
          ref={scrollContainerRef}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            gap: '12px',
            overflowX: 'auto',
            overflowY: 'hidden',
            // 移除 scrollSnapType 以避免瀏覽器自動滾動頁面來對齊元素
            // scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: '16px',
            paddingTop: '8px',
            paddingLeft: '12px',
            paddingRight: '16px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            touchAction: 'pan-x pan-y', // 允許橫向和縱向滾動，讓用戶可以垂直滾動頁面
            overscrollBehaviorX: 'contain', // 防止橫向滾動溢出觸發頁面導航或滾動
          }}
          className="hide-scrollbar"
        >
          {group.items.map((it: any, indexInGroup: number) => {
            let gIndex = 0;
            for (let i = 0; i < groupIndex; i++) {
              gIndex += groupedByTime[i].items.length;
            }
            gIndex += indexInGroup;
            const idx = gIndex;
            const srcIndex = items.indexOf(it);

            return (
              <div 
                key={it.id || idx}
                style={{
                  flex: '0 0 calc(100vw - 60px - 40px)',
                  maxWidth: '350px',
                  minWidth: '260px',
                  scrollSnapAlign: 'start',
                  position: 'relative',
                }}
              >
                {/* 分頁指示器 - 顯示在圖片底部 */}
                {totalItems > 1 && (
                  <div style={{
                    position: 'absolute',
                    top: 145,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10,
                    pointerEvents: 'none',
                  }}>
                    <div style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 500,
                      padding: '4px 12px',
                      borderRadius: 12,
                    }}>
                      {indexInGroup + 1} / {totalItems}
                    </div>
                  </div>
                )}
                <TimelineItem
                  it={it}
                  index={idx}
                  arr={arr}
                  sourceIndex={srcIndex}
                  isEditing={isEditing}
                  hideTimeColumn={true}
                  editor={editor}
                  selectedDay={selectedDay}
                  expandedCards={expandedCards}
                  toggleExpand={(cardId: string) => toggleExpand(cardId)}
                  setOpenPoi={(payload: any) => {
                    // payload 可能是 { visible, payload, dayIndex, itemIndex, ... } 的結構
                    const rawPayload = payload && typeof payload === 'object' ? (payload.payload ?? payload) : undefined;
                    const isFromPlaceholder = Boolean(
                      payload && (
                        payload._fromPlaceholder === true ||
                        (payload.payload && payload.payload._fromPlaceholder === true)
                      )
                    );
                    // 從 payload 中取得 itemIndex（如果有的話），否則使用 srcIndex
                    const itemIndexFromPayload = payload && typeof payload.itemIndex === 'number' ? payload.itemIndex : srcIndex;
                    setOpenPoi(it, itemIndexFromPayload, {
                      fromPlaceholder: isFromPlaceholder,
                      payloadOverride: isFromPlaceholder ? rawPayload : undefined,
                    });
                  }}
                  setInlineEditTarget={setInlineEditTarget}
                  inlineEditTarget={inlineEditTarget}
                  dayIndex={editor.selectedDayIndex}
                  parseTimeToMinutes={parseTimeToMinutes}
                  getNowMinutes={getNowMinutes}
                  isDraggable={false}
                  supabaseClient={supabaseClient}
                  currentUserId={currentUserId}
                  canEdit={canEdit}
                  groupIdForLocal={groupIdForLocal}
                  memberProfiles={memberProfiles}
                  onSelectFromCardPicker={onSelectFromCardPicker}
                  onImageClick={onImageClick}
                />
                {/* 每個卡片下方的筆記佔位按鈕 */}
                <div
                  onClick={() => setOpenInsertIndex(idx)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpenInsertIndex(idx); }}
                  style={{
                    marginTop: 12,
                    marginRight: 26,
                    display: openInsertIndex === idx ? 'none' : 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 18px',
                    borderRadius: 28,
                    background: '#fff',
                    border: '1px solid rgba(230,230,230,1)',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
                    cursor: 'pointer',
                    color: '#9CA3AF',
                    fontSize: 14,
                  }}
                >
                  <span style={{ fontSize: 16, opacity: 0.9 }}>✎</span>
                  <span style={{ color: '#9CA3AF' }}>记录心情、待办、小事项...</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* NoteEditor 獨立區塊 - 在整個橫向區域下方，不受時間軸結構影響 */}
      {isOpenInThisGroup && openInsertIndex !== null && (
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          marginTop: 12, 
          marginBottom: 6,
        }}>
          <NoteEditor
            initialText=""
            saving={insertSaving}
            onCancel={onCancelNote}
            // Pass card id when available; fallback to the itinerary entry id so
            // transport cards (which often don't link to cards_cards) still get
            // image upload UI and uploads are associated with the entry.
            cardId={
              displayedItems[openInsertIndex]?.card?.id
              || displayedItems[openInsertIndex]?.card_id
              || displayedItems[openInsertIndex]?.id
              || undefined
            }
            currentUserId={currentUserId}
            currentItineraryId={groupIdForLocal || undefined}
            gallery={displayedItems[openInsertIndex]?.card?.image_gallery || []}
            onGalleryUpdate={onGalleryUpdateFactory(openInsertIndex)}
            onSave={(data) => onSaveNote(openInsertIndex, data)}
          />
        </div>
      )}
      </>
    );
  }

  // 寬屏：輪播模式，用左右箭頭切換
  return (
    <div style={{ position: 'relative', marginBottom: '8px' }}>
      {/* 當前顯示的卡片 */}
      <TimelineItem
        it={currentItem}
        index={index}
        arr={arr}
        sourceIndex={sourceIndex}
        isEditing={isEditing}
        hideTimeColumn={false}
        editor={editor}
        selectedDay={selectedDay}
        expandedCards={expandedCards}
        toggleExpand={(cardId: string) => toggleExpand(cardId)}
        setOpenPoi={(payload: any) => {
          const rawPayload = payload && typeof payload === 'object' ? (payload.payload ?? payload) : undefined;
          const isFromPlaceholder = Boolean(
            payload && (
              payload._fromPlaceholder === true ||
              (payload.payload && payload.payload._fromPlaceholder === true)
            )
          );
          // 從 payload 中取得 itemIndex（如果有的話），否則使用 sourceIndex
          const itemIndexFromPayload = payload && typeof payload.itemIndex === 'number' ? payload.itemIndex : sourceIndex;
          setOpenPoi(currentItem, itemIndexFromPayload, {
            fromPlaceholder: isFromPlaceholder,
            payloadOverride: isFromPlaceholder ? rawPayload : undefined,
          });
        }}
        setInlineEditTarget={setInlineEditTarget}
        inlineEditTarget={inlineEditTarget}
        dayIndex={editor.selectedDayIndex}
        parseTimeToMinutes={parseTimeToMinutes}
        getNowMinutes={getNowMinutes}
        isDraggable={false}
        supabaseClient={supabaseClient}
        currentUserId={currentUserId}
        canEdit={canEdit}
        groupIdForLocal={groupIdForLocal}
        memberProfiles={memberProfiles}
        onSelectFromCardPicker={onSelectFromCardPicker}
        onImageClick={onImageClick}
      />
      
      {/* 左右箭頭 - 固定在圖片區域（從頂部計算固定位置） */}
      {totalItems > 1 && (
        <>
          {/* 左箭頭 - 固定在頂部往下約 120px（圖片區域中間） */}
          <button
            onClick={goToPrev}
            disabled={currentIndex === 0}
            style={{
              position: 'absolute',
              left: 70,
              top: 120,
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: 'none',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              color: '#fff',
              fontSize: 48,
              fontWeight: 'bold',
              lineHeight: '36px',
              cursor: currentIndex === 0 ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              opacity: currentIndex === 0 ? 0.4 : 1,
            }}
          >
            <MdChevronLeft size={28} color="#fff" />
          </button>
          
          {/* 右箭頭 - 固定在頂部往下約 120px（圖片區域中間） */}
          <button
            onClick={goToNext}
            disabled={currentIndex === totalItems - 1}
            style={{
              position: 'absolute',
              right: 20,
              top: 120,
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: 'none',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              color: '#fff',
              fontSize: 48,
              fontWeight: 'bold',
              lineHeight: '36px',
              cursor: currentIndex === totalItems - 1 ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              opacity: currentIndex === totalItems - 1 ? 0.4 : 1,
            }}
          >
            <MdChevronRight size={28} color="#fff" />
          </button>
          
          {/* 頁碼指示器 - 放在圖片底部 */}
          <div style={{
            position: 'absolute',
            top: 200,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 6,
            zIndex: 10,
          }}>
            {group.items.map((_, i) => (
              <div
                key={i}
                onClick={() => setCurrentIndex(i)}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: i === currentIndex ? '#F97316' : 'rgba(0, 0, 0, 0.3)',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </>
      )}
      
      {/* 筆記輸入框 */}
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', marginTop: 12, marginBottom: 32 }}>
        <div style={{ width: 60 }} />
        <div style={{ flex: 1, paddingLeft: 12, paddingRight: 20 }}>
          <div
            onClick={() => setOpenInsertIndex(index)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpenInsertIndex(index); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 18px',
              borderRadius: 28,
              background: '#fff',
              border: '1px solid rgba(230,230,230,1)',
              boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
              cursor: 'pointer',
              color: '#9CA3AF',
              fontSize: 14,
            }}
          >
            <span style={{ fontSize: 16, opacity: 0.9 }}>✎</span>
            <span style={{ color: '#9CA3AF' }}>记录心情、待办、小事项...</span>
          </div>
        </div>
      </div>

      {openInsertIndex === index && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32, paddingLeft: 60, paddingRight: 20 }}>
          <NoteEditor
            initialText=""
            saving={insertSaving}
            onCancel={onCancelNote}
            cardId={
              displayedItems[index]?.card?.id
              || displayedItems[index]?.card_id
              || displayedItems[index]?.id
              || undefined
            }
            currentUserId={currentUserId}
            currentItineraryId={groupIdForLocal || undefined}
            gallery={displayedItems[index]?.card?.image_gallery || []}
            onGalleryUpdate={onGalleryUpdateFactory(index)}
            onSave={(data) => onSaveNote(index, data)}
          />
        </div>
      )}
    </div>
  );
};

type TimelineListProps = {
  items: any[];
  selectedDay: any;
  isNarrow: boolean;
  isEditing?: boolean;
  parseTimeToMinutes: (t?: string) => number | undefined;
  getNowMinutes: () => number;
  expandedCards: Set<string>;
  toggleExpand: (id: string) => void;
  editor: any;
  setOpenPoi: (item: any, itemIndex: number, context?: { fromPlaceholder?: boolean; payloadOverride?: any }) => void;
  setInlineEditTarget?: (t: any) => void;
  setEditItemIndex?: (i: number | null) => void;
  setEditForm?: (f: any) => void;
  setEditModalVisible?: (v: boolean) => void;
  inlineEditTarget?: { dayIndex: number; itemIndex: number; mode?: string } | null;
  setDirty?: (v: boolean) => void;
  // persistence & permissions context forwarded from ItineraryEditor
  supabaseClient?: any;
  currentUserId?: string | null;
  canEdit?: boolean;
  groupIdForLocal?: string | null;
  memberProfiles?: Record<string, { display_name?: string | null; avatar_url?: string | null }>;
  // 占位卡片选择 CardPicker 的回调
  onSelectFromCardPicker?: (dayIndex: number, itemIndex: number) => void;
  // 指令：滚动到指定 index 的卡片（基于当前 displayedItems 次序）
  scrollToIndex?: number | null;
  // 图片灯箱回调
  onImageClick?: (images: any[], startIndex: number) => void;
};
const TimelineList: React.FC<TimelineListProps> = ({
  items,
  selectedDay,
  isNarrow,
  isEditing = false,
  parseTimeToMinutes,
  getNowMinutes,
  expandedCards,
  toggleExpand,
  editor,
  setOpenPoi,
  setInlineEditTarget,
  setEditItemIndex,
  setEditForm,
  setEditModalVisible,
  inlineEditTarget,
  setDirty,
  supabaseClient,
  currentUserId,
  canEdit,
  groupIdForLocal,
  memberProfiles,
  onSelectFromCardPicker,
  scrollToIndex,
  onImageClick,
}) => {
  const [tooltipVisible, setTooltipVisible] = useState<Record<string, boolean>>({});
  const [selectedCardForDrag, setSelectedCardForDrag] = useState<string | null>(null);
  
  const sorted = useMemo(() => {
    const rawItems = Array.isArray(items) ? items : [];
    
    const topLevelItems = rawItems.filter((item) => {
      // 只检查直接的 parent_entry_id 字段（数据库字段）
      // 不检查 meta 和 payload，避免误判
      const parentId = item?.parent_entry_id || item?.parentEntryId || null;
      
      // 如果有父级 ID，说明是二級項目（child note），不應顯示在主時間軸
      if (parentId) {
        console.log('[TimelineList FILTER] ✅ 过滤二级项目（有 parent_entry_id）:', { 
          id: item?.id,
          title: item?.title, 
          type: item?.type,
          parentId,
        });
        return false;
      }
      
      console.log('[TimelineList FILTER] ✓ 保留顶层项目:', {
        id: item?.id,
        title: item?.title,
        type: item?.type,
        card_id: item?.card_id,
      });
      
      return true;
    });

    console.log('[TimelineList SORTED] 開始排序，輸入 items:', rawItems.length);
    console.log('[TimelineList SORTED] 過濾後的頂層項目:', topLevelItems.length, topLevelItems.map(i => ({ title: i?.title, time: i?.time })));

    const normalizedItems = topLevelItems.map(item => {
      const timeMatch = item.time?.match(/^\d{1,2}:\d{2}$/);
      if (!item.time || typeof item.time !== 'string' || !timeMatch) {
        console.warn('[TimelineList] Item missing or invalid time field, using default 08:00', {
          itemId: item?.id,
          itemTitle: item?.title,
          originalTime: item?.time,
          timeMatch,
        });
        return { ...item, time: '08:00' };
      }
      return item;
    });

    const sortedItems = normalizedItems
      .slice()
      .sort((a: any, b: any) => compareTimelineItems(a, b, parseTimeToMinutes));

    console.log('[TimelineList SORTED] 排序完成', {
      数量: sortedItems.length,
      顺序: sortedItems.map(item => `${item.title}(${item.time})`),
    });

    return sortedItems;
  }, [items, parseTimeToMinutes]);

  // 2️⃣ 本地卡片顺序映射（用于拖拽时追踪卡片位置变化）
  const [cardOrderMapping, setCardOrderMapping] = useState<string[]>([]);
  const [forceRerenderKey, setForceRerenderKey] = useState(0);
  const [scrollTargetEntryId, setScrollTargetEntryId] = useState<string | null>(null);

  const sanitizeNoteImages = useCallback((images?: any[]) => {
    if (!Array.isArray(images)) return [];
    const seen = new Set<string>();
    const result: any[] = [];
    images.forEach((img) => {
      if (!img) return;
      const normalized = {
        ...img,
        uploaded_at: img.uploaded_at || new Date().toISOString(),
      };
      const dedupeKey = normalized.key ? `key:${normalized.key}` : (normalized.url ? `url:${normalized.url}` : `idx:${result.length}`);
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      result.push(normalized);
    });
    return result;
  }, []);

  const patchNoteImagesLocally = useCallback((entryId: string, images: any[], galleryForCard?: any[]) => {
    const sanitized = sanitizeNoteImages(images);
    if (!entryId || typeof editor?.setItinerary !== 'function') {
      return { sanitized, patched: false };
    }

    const targetId = String(entryId);
    let patched = false;

    editor.setItinerary((prev: any) => {
      if (!prev || !Array.isArray(prev.days)) {
        console.warn('[TimelineList] 未找到要更新图片的子笔记（itinerary 不存在）', { entryId: targetId });
        return prev;
      }

      const next = JSON.parse(JSON.stringify(prev));
      let found = false;

      outer: for (let dayIdx = 0; dayIdx < (next.days || []).length; dayIdx += 1) {
        const day = next.days[dayIdx];
        const items = Array.isArray(day?.items) ? day.items : [];
        for (let itemIdx = 0; itemIdx < items.length; itemIdx += 1) {
          const item = items[itemIdx];
          const children = Array.isArray(item?.children) ? item.children : [];
          for (let childIdx = 0; childIdx < children.length; childIdx += 1) {
            const child = children[childIdx];
            const cid = String(
              child?.id ||
              child?.meta?.itineraryEntryId ||
              child?.meta?.itinerary_entry_id ||
              ''
            );
            if (cid && cid === targetId) {
              const prevPayload = child.custom_payload || child.payload || {};
              const mergedPayload = { ...prevPayload, images: sanitized };
              child.custom_payload = mergedPayload;
              child.payload = mergedPayload;
              child.local_images = sanitized;
              if (Array.isArray(galleryForCard) && item?.card) {
                item.card.image_gallery = galleryForCard;
              }
              found = true;
              break outer;
            }
          }
        }
      }

      if (!found) {
        console.warn('[TimelineList] 未找到要更新图片的子笔记', { entryId: targetId });
        return prev;
      }

      patched = true;
      return { ...next, _updateTimestamp: Date.now() };
    });

    return { sanitized, patched };
  }, [editor, sanitizeNoteImages]);

  useEffect(() => {
    if (!scrollTargetEntryId || typeof document === 'undefined') return;

    let rafId: number | null = null;
    let attempts = 0;
    const maxAttempts = 10;

    const scrollIntoView = () => {
      const targetEl = document.querySelector(`[data-note-entry-id="${scrollTargetEntryId}"]`);
      if (targetEl && 'scrollIntoView' in targetEl) {
        (targetEl as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        setScrollTargetEntryId(null);
        return;
      }
      if (attempts < maxAttempts) {
        attempts += 1;
        rafId = window.requestAnimationFrame(scrollIntoView);
      }
    };

    rafId = window.requestAnimationFrame(scrollIntoView);
    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [scrollTargetEntryId, editor?.itinerary?._updateTimestamp]);
  
  // 当 sorted 改变时，重置映射（确保新卡片被正确包含）
  useEffect(() => {
    console.log('[TimelineList] 重置 cardOrderMapping，因为 sorted 改变');
    setCardOrderMapping(sorted.map(item => item.id));
  }, [sorted]);

  // 3️⃣ 根据映射显示卡片（支持拖拽后的排序变化）
  const displayedItems = useMemo(() => {
    // 如果没有映射或映射长度不匹配，直接使用 sorted
    if (!cardOrderMapping.length || cardOrderMapping.length !== sorted.length) {
      console.log('[displayedItems] 映射不同步，使用 sorted 陣列');
      return sorted;
    }

    // 根据映射重新排列卡片
    const mapped = cardOrderMapping
      .map(cardId => sorted.find(item => item.id === cardId))
      .filter(Boolean) as any[];
    
    console.log('[✓ displayedItems 已更新]', {
      映射顺序: mapped.map((item: any) => `${item?.title}(${item?.time})`)
    });
    
    return mapped;
  }, [cardOrderMapping, sorted]);

  // 4️⃣ 按时间分组：相同时间的 items 会被分到同一组，用于横向排列显示
  const groupedByTime = useMemo(() => {
    const groups: { time: string; items: any[] }[] = [];
    let currentGroup: { time: string; items: any[] } | null = null;

    // 先打印所有 items 的 time 值，用于调试
    console.log('[groupedByTime] displayedItems times:', displayedItems.map((it: any) => ({
      title: it?.title?.substring(0, 10),
      time: it?.time,
      rawTime: JSON.stringify(it?.time),
    })));

    displayedItems.forEach((item: any) => {
      const time = item?.time || '08:00';
      if (!currentGroup || currentGroup.time !== time) {
        // 新时间组
        currentGroup = { time, items: [item] };
        groups.push(currentGroup);
      } else {
        // 同时间，加入当前组
        currentGroup.items.push(item);
      }
    });

    console.log('[groupedByTime] 结果:', groups.map(g => `${g.time}: ${g.items.length}个`));
    return groups;
  }, [displayedItems]);

  const [draggedItem, setDraggedItem] = useState<any>(null);
  const [openInsertIndex, setOpenInsertIndex] = useState<number | null>(null);
  const [insertSaving, setInsertSaving] = useState(false);
  const { showSuccess, showError, showWarning, showInfo } = useGlobalToast();
  // confirm modal state for delete (iOS-style dialog)
  // Store the UNDERLYING items[] index for delete to avoid mismatches with displayed order
  const [confirmDelete, setConfirmDelete] = useState<{ visible: boolean; sourceIndex?: number; item?: any }>(() => ({ visible: false }));
  // 登录提示模态框状态（当匿名用户尝试写入时显示）
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const performDeleteFor = useCallback(async (item: any, idx?: number) => {
    try {
      const dayIdx = editor.selectedDayIndex;
      // 如果有 card_id 且有 supabase client，执行服务器端删除
      const cardId = item?.card_id || item?.cardId || item?.id;
      if (cardId && supabaseClient && canEdit) {
        try {
          const { error } = await supabaseClient
            .from('cards_cards')
            .delete()
            .eq('card_id', cardId);

          if (error) {
            console.error('[performDeleteFor] Supabase delete error', error);
          }
        } catch (e) {
          console.error('[performDeleteFor] server delete unexpected', e);
        }
      }

      // 本地删除
      if (editor && typeof editor.deleteItem === 'function' && typeof idx === 'number') {
        editor.deleteItem(dayIdx, idx);
      }

      setDirty && setDirty(true);
      try { showSuccess && showSuccess('已删除该卡片'); } catch (er) {}
    } catch (e) {
      console.error('[performDeleteFor] delete flow failed', e);
      try { showError && showError('删除失败'); } catch (er) {}
    }
  }, [editor, supabaseClient, canEdit, setDirty, showSuccess, showError]);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [handTop, setHandTop] = useState<number>(32);
  const [handLeft, setHandLeft] = useState<number>(56);
  const [scrollLocked, setScrollLocked] = useState(false);
  
  // 確保在切換到非編輯模式時清除任何滾動鎖定狀態
  useEffect(() => {
    if (!isEditing && typeof document !== 'undefined') {
      // 清除可能殘留的 body 樣式
      if (document.body.style.position === 'fixed') {
        const scrollY = parseInt(document.body.style.top || '0', 10);
        console.debug('[TimelineList][SCROLL] clearing fixed body styles, stored top:', scrollY);
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        window.scrollTo(0, -scrollY);
      }
      setScrollLocked(false);
      setIsDragging(false);
      setDraggedItem(null);
      setDragOffsetY(0);
    }
  }, [isEditing]);
  
  const dragStartYRef = useRef<number>(0);
  const itemRefsRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const wasDraggedRef = useRef(false);
  const pointerDownSuppressRef = useRef<string | null>(null);
  // track whether the pointerdown actually changed selection for the target card
  const pointerDownSelectedChangedRef = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null); // 长按计时器
  const longPressTriggeredRef = useRef(false); // 标记是否触发了长按
  const pendingDragItemRef = useRef<any>(null); // 待拖拽的项目

  // 自动滚动逻辑
  const startAutoScroll = useCallback((direction: 'up' | 'down') => {
    if (scrollIntervalRef.current) return;

    const scrollSpeed = 15;
    const scrollStep = direction === 'down' ? scrollSpeed : -scrollSpeed;

    scrollIntervalRef.current = setInterval(() => {
      // 当 body 被 fixed 时，通过修改 top 值来模拟滚动
      if (scrollLocked && document.body.style.position === 'fixed') {
        const currentTop = parseInt(document.body.style.top || '0', 10);
        const newTop = currentTop - scrollStep; // 注意方向是反的
        console.debug('[TimelineList][SCROLL] simulating scroll by adjusting body.top', { currentTop, newTop, scrollStep });
        document.body.style.top = `${newTop}px`;
        setDragOffsetY(prev => prev - scrollStep);
      } else {
        // 正常情况下使用 window.scrollBy
        window.scrollBy(0, scrollStep);
        setDragOffsetY(prev => prev - scrollStep);
      }
    }, 16);
  }, [scrollLocked]);

  const stopAutoScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  // 当外部指令要求滚动到某个 index 时，尝试把对应元素滚到视窗中间
  useEffect(() => {
    if (typeof scrollToIndex !== 'number') return;
    const el = itemRefsRef.current.get(scrollToIndex);
    if (el && el.scrollIntoView) {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch {
        try { el.scrollIntoView(); } catch {}
      }
    }
  // 依赖 displayedItems.length，确保列表渲染完成后再尝试滚动
  }, [scrollToIndex, displayedItems.length]);

  // 拖拽检测逻辑
  const handleGlobalPointerMove = useCallback((e: PointerEvent) => {
    if (!isDragging || !draggedItem) return;
    
    // 真正拖拽时才阻止默认行为
    e.preventDefault();
    e.stopPropagation();

    const currentY = e.clientY;
    const offsetY = currentY - dragStartYRef.current;
    setDragOffsetY(offsetY);

    // 自动滚动检测
    const scrollThreshold = 100;
    const viewportHeight = window.innerHeight;
    
    if (currentY < scrollThreshold) {
      startAutoScroll('up');
    } else if (currentY > viewportHeight - scrollThreshold) {
      startAutoScroll('down');
    } else {
      stopAutoScroll();
    }

    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const relativeY = currentY - containerRect.top;
    const itemHeight = containerRect.height / Math.max(1, sorted.length);
    
    let targetSlotIndex = Math.floor(relativeY / itemHeight);
    targetSlotIndex = Math.max(0, Math.min(targetSlotIndex, sorted.length - 1));
    
    const currentDraggedSlotIndex = cardOrderMapping.indexOf(draggedItem.id);
    
    if (targetSlotIndex === currentDraggedSlotIndex) {
      setDragOverIndex(null);
      return;
    }

    console.log('[拖拽中]', { 当前槽位: currentDraggedSlotIndex, 目标槽位: targetSlotIndex });
    setDragOverIndex(targetSlotIndex);
  }, [isDragging, draggedItem, cardOrderMapping, sorted, startAutoScroll, stopAutoScroll, scrollLocked]);

  const handleGlobalPointerUp = useCallback(() => {
    console.log('[放手触发]', { scrollLocked });

    // 清除长按计时器
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    pendingDragItemRef.current = null;

    stopAutoScroll();

    // 恢复页面滚动（只有在真正锁定过的情况下才恢复）
    if (scrollLocked && typeof document !== 'undefined') {
      const scrollY = parseInt(document.body.style.top || '0', 10);
      console.debug('[TimelineList][SCROLL] restoring body styles, stored top:', scrollY);
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, -scrollY);
      setScrollLocked(false);
    }

    // ============================================
    // 🎯 核心逻辑：交换时间槽里的卡片
    // ============================================
    
    setIsDragging(false);
    setDraggedItem(null);
    setDragOffsetY(0);
    
    if (dragOverIndex !== null && draggedItem) {
      const currentDraggedSlotIndex = cardOrderMapping.indexOf(draggedItem.id);
      
      console.log('[放手计算]', {
        draggedId: draggedItem.id,
        draggedTitle: draggedItem.title,
        从槽位: currentDraggedSlotIndex,
        到槽位: dragOverIndex,
        当前映射: cardOrderMapping.map((id, i) => `槽${i}:${sorted.find(it => it.id === id)?.title}`)
      });
      
      if (currentDraggedSlotIndex !== -1 && dragOverIndex !== currentDraggedSlotIndex) {
        console.log('[✓ 执行交换]', { from: currentDraggedSlotIndex, to: dragOverIndex });
        
        const newMapping = [...cardOrderMapping];
        const temp = newMapping[currentDraggedSlotIndex];
        newMapping[currentDraggedSlotIndex] = newMapping[dragOverIndex];
        newMapping[dragOverIndex] = temp;
        
        console.log('[新映射]', { 
          新映射: newMapping.map((id, i) => `槽${i}:${sorted.find(it => it.id === id)?.title}`)
        });
        
        setCardOrderMapping(newMapping);
        setForceRerenderKey(prev => prev + 1);
        
        if (editor && editor.itinerary && editor.itinerary.days) {
          const updatedDays = editor.itinerary.days.map((day: any, dayIdx: number) => {
            if (dayIdx !== editor.selectedDayIndex) return day;
            
            const updatedItems = (day.items || []).map((item: any) => {
              const slotIndex = newMapping.indexOf(item.id);
              if (slotIndex !== -1 && sorted[slotIndex]) {
                return {
                  ...item,
                  time: sorted[slotIndex].time
                };
              }
              return item;
            });
            
            return {
              ...day,
              items: updatedItems
            };
          });
          
          editor.setItinerary({ ...editor.itinerary, days: updatedDays });
          console.log('[✓ 编辑器数据已更新]', { 新的时间映射: newMapping.map((id, i) => `${i}:${sorted[i]?.time}`) });
        }
        
        if (setDirty) {
          setDirty(true);
        } else if (editor && editor.setDirty) {
          editor.setDirty(true);
        }
        
        if (typeof document !== 'undefined') {
          const msg = document.createElement('div');
          msg.textContent = `✅ 已调整顺序（记得保存）`;
          msg.style.cssText = `
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: #4CAF50;
            color: white;
            padding: 12px 24px;
            border-radius: 6px;
            z-index: 9999;
            font-size: 14px;
            font-weight: 600;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            animation: slideDown 0.3s ease;
          `;
          
          const style = document.createElement('style');
          style.textContent = `
            @keyframes slideDown {
              from { transform: translateX(-50%) translateY(-100%); opacity: 0; }
              to { transform: translateX(-50%) translateY(0); opacity: 1; }
            }
          `;
          document.head.appendChild(style);
          
          document.body.appendChild(msg);
          setTimeout(() => {
            msg.remove();
            style.remove();
          }, 2000);
        }
      }
    }
    
    setDragOverIndex(null);
    console.log('[放手] 完成');
  }, [draggedItem, dragOverIndex, cardOrderMapping, sorted, editor, stopAutoScroll, scrollLocked]);

  // 事件监听器设置
  useEffect(() => {
    if (!isDragging) {
      stopAutoScroll();
      return;
    }

    const handleMove = (e: PointerEvent) => handleGlobalPointerMove(e);
    const handleUp = () => handleGlobalPointerUp();

    window.addEventListener('pointermove', handleMove, { passive: false });
    window.addEventListener('pointerup', handleUp, { passive: false });

    return () => {
      stopAutoScroll();
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [isDragging, handleGlobalPointerMove, handleGlobalPointerUp, stopAutoScroll]);

  // 处理指针按下（开始长按检测）
  const handlePointerDown = useCallback((e: React.PointerEvent, item: any, index: number, uid: string) => {
    console.log('[handlePointerDown 被调用]', { item: item.title, index, isEditing, uid });
    
    // If user clicked on an interactive element (input/select/textarea/button/contenteditable),
    // don't start a drag — allow inline interactions like typing.
    try {
      const target = (e.target as HTMLElement) || (e.nativeEvent && (e.nativeEvent as any).target);
      if (target) {
        const closestInteractive = (target as HTMLElement).closest && (target as HTMLElement).closest('input, textarea, select, button, a, [contenteditable="true"]');
        if (closestInteractive) {
          console.log('[handlePointerDown] 点击了交互元素，忽略');
          return;
        }
      }
    } catch (err) {
      // ignore and continue
    }
    if (!isEditing) {
      console.log('[handlePointerDown] 不在编辑模式，忽略');
      return;
    }
    
    console.log('[指针按下 - 开始长按检测]', { 
      item: item.title, 
      index,
      time: item.time
    });
    
    // 重置长按标记
    longPressTriggeredRef.current = false;

    // 🎯 不阻止默认行为，允许正常滚动
    // 记录初始位置和待拖拽项
    dragStartYRef.current = e.clientY;
    pendingDragItemRef.current = { ...item };
    
    // 启动长按计时器（500ms）
    longPressTimerRef.current = setTimeout(() => {
      console.log('[长按触发 - 开始拖拽]', { item: item.title });
      
      // 标记长按已触发
      longPressTriggeredRef.current = true;

      // 长按触发，选中卡片并开始拖拽
      setSelectedCardForDrag(uid);
      setTooltipVisible(prev => ({ ...prev, [`tooltip-${uid}`]: true }));

      wasDraggedRef.current = false;
      setDraggedItem({ ...item });
      setIsDragging(true);
      setDragOffsetY(0);
      
      // 禁用页面滚动
      if (typeof document !== 'undefined') {
        try {
          const scrollY = window.scrollY || window.pageYOffset || 0;
          document.body.style.top = `-${Math.ceil(scrollY)}px`;
          console.debug('[TimelineList][SCROLL] lock body fixed top ->', Math.ceil(scrollY));
          document.body.style.position = 'fixed';
          document.body.style.width = '100%';
          document.documentElement.style.overflow = 'hidden';
          document.body.style.overflow = 'hidden';
          setScrollLocked(true);
          
          // 给用户一个触觉反馈（如果支持）
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }
        } catch (err) {
          // ignore
        }
      }
    }, 500); // 500ms 长按阈值
  }, [isEditing]);
  
  // 处理指针移动 - 不再取消长按，因为拖拽需要移动
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    // 不做任何事情，让拖拽手柄可以自由移动
  }, []);
  
  // 处理指针抬起（取消长按检测）
  const handlePointerUpLocal = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      pendingDragItemRef.current = null;
      console.log('[指针抬起 - 取消长按]');
    }
  }, []);

  const measureHandPosition = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!isEditing) return;
    const containerEl = containerRef.current;
    const firstItemEl = itemRefsRef.current.get(0);
    if (!containerEl || !firstItemEl) return;

    const containerRect = containerEl.getBoundingClientRect();
    const firstRect = firstItemEl.getBoundingClientRect();

  const nextTop = Math.max(88, firstRect.top - containerRect.top + 88);
  const nextLeft = Math.max(48, firstRect.left - containerRect.left + 28);

    setHandTop(nextTop);
    setHandLeft(nextLeft);
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    if (typeof window === 'undefined') return;

    const handleResize = () => measureHandPosition();
    const rafId = requestAnimationFrame(measureHandPosition);
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, [isEditing, displayedItems.length, forceRerenderKey, measureHandPosition]);

  // 🎯 生成一个映射的 hash，用于强制整个 div 重新渲染
  const cardMappingHash = `${cardOrderMapping.join(',')}__${forceRerenderKey}`;
  const hasTimelineItems = displayedItems.length > 0;

  // 笔记保存处理函数 - 在编辑和非编辑模式下都可用
  const handleSaveNote = async (index: number, data: { text: string; mapLink?: string; mapLinkMode?: string; mapLinkDuration?: string }) => {
    const { text, mapLink, mapLinkMode, mapLinkDuration } = data;
    try {
      setInsertSaving(true);
      const afterItem = displayedItems[index];
      const afterEntryId = afterItem ? (afterItem.id || afterItem.meta?.itineraryEntryId || null) : null;
      const parentEntryId = afterItem ? (afterItem.meta?.itineraryEntryId || afterItem.id || null) : null;
      const existingChildrenCount = afterItem && Array.isArray(afterItem.children) ? afterItem.children.length : 0;
      const canPersistChild = Boolean(afterItem?.meta?.itineraryEntryId);
      const dayIndex = editor.selectedDayIndex || 0;
      const dayNumber = (dayIndex || 0) + 1;
      const dayDate = selectedDay && (selectedDay.date || selectedDay.date_str) ? (selectedDay.date || selectedDay.date_str) : null;
      const link = typeof mapLink === 'string' ? mapLink.trim() : '';
      const normalizedMapLink = link || undefined;
      const normalizedMode = normalizedMapLink ? (mapLinkMode || '').trim() : '';
      const normalizedDuration = normalizedMapLink ? (mapLinkDuration || '').trim() : '';
      
      const inserted = canPersistChild
        ? await addNoteEntry({
            supabaseClient,
            groupId: groupIdForLocal || '',
            dayNumber,
            dayDate,
            afterEntryId,
            creatorId: currentUserId || null,
            noteText: text,
            parentEntryId,
            childSort: existingChildrenCount,
            mapLink: normalizedMapLink,
            mapLinkMode: normalizedMode || null,
            mapLinkDuration: normalizedDuration || null,
          })
        : null;

      const itCopy: any = JSON.parse(JSON.stringify(editor.itinerary || { days: [] }));
      while ((itCopy.days || []).length <= dayIndex) itCopy.days.push({ day: (itCopy.days || []).length + 1, items: [] });
      const itemsArr: any[] = itCopy.days[dayIndex].items || [];
      const nowIso = new Date().toISOString();
      const persistedCreated = inserted?.created_at || nowIso;
      const persistedUpdated = inserted?.updated_at || persistedCreated;
      const writerId = inserted?.creator_id || currentUserId || null;
      
      let profileForWriter = writerId && memberProfiles ? (memberProfiles as any)[String(writerId)] : undefined;
      if (!profileForWriter && currentUserId && memberProfiles) {
        profileForWriter = (memberProfiles as any)[String(currentUserId)];
      }
      
      const resolveKnownAvatar = (): string | null => {
        try {
          const items = (editor.itinerary?.days?.[dayIndex]?.items || []) as any[];
          for (const it of items) {
            const itCreator = it?.creator_id || it?.creatorId || it?.meta?.creatorId;
            if (itCreator && String(itCreator) === String(writerId)) {
              const hit = it?.creator_avatar_url || it?.creator_profile?.avatar_url || it?.memberProfile?.avatar_url;
              if (hit) return String(hit);
            }
            const children = Array.isArray(it?.children) ? it.children : [];
            for (const ch of children) {
              const chCreator = ch?.creator_id || ch?.creatorId || ch?.meta?.creatorId;
              if (chCreator && String(chCreator) === String(writerId)) {
                const hit2 = ch?.creator_avatar_url || ch?.creator_profile?.avatar_url || ch?.memberProfile?.avatar_url;
                if (hit2) return String(hit2);
              }
            }
          }
        } catch {}
        return null;
      };
      const avatarResolved = (profileForWriter && profileForWriter.avatar_url) ? profileForWriter.avatar_url : resolveKnownAvatar();
      if (avatarResolved && profileForWriter && !profileForWriter.avatar_url) {
        profileForWriter = { ...profileForWriter, avatar_url: avatarResolved } as any;
      }

      const payloadForNote = inserted?.custom_payload || {
        ...(normalizedMapLink ? { mapLink: normalizedMapLink } : {}),
        ...(normalizedMode ? { mapLinkMode: normalizedMode } : {}),
        ...(normalizedDuration ? { mapLinkDuration: normalizedDuration } : {}),
      };

      const tryAttachToParent = () => {
        if (!parentEntryId) return false;
        const parentIndex = itemsArr.findIndex((x: any) =>
          String(x.id) === String(parentEntryId) || String(x?.meta?.itineraryEntryId) === String(parentEntryId)
        );
        if (parentIndex < 0) return false;
        const parent = itemsArr[parentIndex];
        if (!parent) return false;
        if (!Array.isArray(parent.children)) parent.children = [];
        
        const newChildId = inserted?.id || `temp-${Date.now()}`;
        const newChild: any = {
          id: newChildId,
          type: 'note',
          note: text,
          payload: payloadForNote,
          custom_payload: payloadForNote,
          parentEntryId: parentEntryId,
          created_at: persistedCreated,
          updated_at: persistedUpdated,
          creator_id: writerId,
          creator_profile: profileForWriter,
          creatorProfile: profileForWriter,
          creator_name: profileForWriter?.display_name || null,
          creator_avatar_url: (profileForWriter && profileForWriter.avatar_url) ? profileForWriter.avatar_url : null,
          memberProfile: profileForWriter || null,
          meta: {
            itineraryEntryId: inserted?.id || null,
            itemType: 'note',
            parentEntryId: parentEntryId,
            childSort: existingChildrenCount,
            creatorId: writerId,
          },
        };
        
        if (parent.card) newChild.card = parent.card;
        
        parent.children.push(newChild);
        setScrollTargetEntryId(String(newChildId));
        return true;
      };

      const attached = tryAttachToParent();
      if (!attached) {
        const newItemId = inserted?.id || `temp-${Date.now()}`;
        itemsArr.push({
          id: newItemId,
          type: 'note',
          note: text,
          payload: payloadForNote,
          custom_payload: payloadForNote,
          parentEntryId: parentEntryId,
          created_at: persistedCreated,
          updated_at: persistedUpdated,
          creator_id: writerId,
          creator_profile: profileForWriter,
          creatorProfile: profileForWriter,
          creator_name: profileForWriter?.display_name || null,
          creator_avatar_url: (profileForWriter && profileForWriter.avatar_url) ? profileForWriter.avatar_url : null,
          memberProfile: profileForWriter || null,
          local_images: [],
          meta: {
            itineraryEntryId: inserted?.id || null,
            itemType: 'note',
            parentEntryId: parentEntryId,
            childSort: existingChildrenCount,
            creatorId: writerId,
          },
        });
        setScrollTargetEntryId(String(newItemId));
      }
      
      const freshCopy = JSON.parse(JSON.stringify(itCopy));
      const updatedIt = { ...freshCopy, _updateTimestamp: Date.now() };

      editor.setItinerary(updatedIt);
      try { setDirty && setDirty(true); } catch (e) {}
      
      return { entryId: inserted?.id };
    } catch (err) {
      console.error('[添加二级笔记失败]', err);
      const errMsg = String((err as any)?.message || '');
      // 检测 RLS (Row-Level Security) 错误，提示用户需要登录
      if (errMsg.includes('row-level security') || errMsg.includes('RLS') || errMsg.includes('policy')) {
        setShowLoginPrompt(true);
      } else {
        try { showError && showError(errMsg || '新增失敗'); } catch (er) {}
      }
      throw err;
    } finally {
      setInsertSaving(false);
      setOpenInsertIndex(null);
    }
  };

  // 图片库更新工厂函数
  const handleGalleryUpdateFactory = (index: number) => {
    if (!supabaseClient || !editor) {
      return undefined;
    }
    return async (updatedGallery: any[], entryId?: string) => {
      try {
        if (!entryId) return;

        const noteImages = Array.isArray(updatedGallery)
          ? updatedGallery
              .filter((img: any) => img && img.entry_id && String(img.entry_id) === String(entryId))
              .map((img: any) => ({
                url: img.url,
                key: img.key,
                uploaded_by: img.uploaded_by,
                uploaded_at: img.uploaded_at || new Date().toISOString(),
                entry_id: img.entry_id,
              }))
          : [];

        const cardGallery = Array.isArray(updatedGallery) ? updatedGallery : undefined;
        const { sanitized } = patchNoteImagesLocally(entryId, noteImages, cardGallery);

        const persistImages = sanitized.filter((img: any) => typeof img.url === 'string' && /^https?:\/\//.test(img.url));
        await supabaseClient
          .from('group_itinerary_entries')
          .update({ custom_payload: { images: persistImages }, inline_payload: { images: persistImages } })
          .eq('id', entryId);

        if (setDirty) setDirty(true);
      } catch (err) {
        console.error('❌ Failed to update note images (custom_payload):', err);
        throw err;
      }
    };
  };
  
  // 如果是编辑模式，使用支持拖放的版本
  if (isEditing) {
    console.log('[TimelineList] 渲染拖放版本，时间槽数量:', sorted.length, '映射hash:', cardMappingHash);
    
    return (
      <div 
        key={`timeline-${cardMappingHash}`}
        ref={containerRef}
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '0px', 
          width: '100%', 
          position: 'relative',
          minHeight: '200px'
        }}
      >
          {/* Confirm delete modal (iOS style) */}
            <ProfileModal
            visible={!!confirmDelete.visible}
            title="删除卡片"
            message="确定要删除这个卡片吗？"
            onCancel={() => setConfirmDelete({ visible: false })}
            onConfirm={async () => {
              try {
                await performDeleteFor(confirmDelete.item, confirmDelete.sourceIndex);
              } finally {
                setConfirmDelete({ visible: false });
              }
            }}
          />
          {/* 登录提示模态框 (iOS style) - 当匿名用户尝试发小记时显示 */}
          <ProfileModal
            visible={showLoginPrompt}
            title="需要登录"
            message="只有注册用户才可以发小记，立即注册开始记录你的旅途吧！"
            cancelText="取消"
            confirmText="注册"
            onCancel={() => setShowLoginPrompt(false)}
            onConfirm={() => {
              setShowLoginPrompt(false);
              // 获取当前页面 URL 作为 referer，登录后返回
              const currentUrl = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
              window.location.href = `/auth?referer=${encodeURIComponent(currentUrl)}`;
            }}
          />
        {/* 移除了浮动的手势提示图标，现在每个卡片都有自己的拖拽手柄 */}
        
        <div 
          key={cardMappingHash}
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '0px', 
            userSelect: 'none',
            position: 'relative'
          }}
        >
          {/* 按时间分组渲染：編輯模式下垂直排列，不使用橫向邏輯 */}
          {groupedByTime.map((group, groupIndex) => {
            console.log(`[渲染分组 ${groupIndex}] time=${group.time}, items=${group.items.length}`);
            
            return (
              <div 
                key={`time-group-${groupIndex}-${group.time}`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0px',
                }}
              >
                {group.items.map((it: any, indexInGroup: number) => {
                  // 计算全局 index
                  let globalIndex = 0;
                  for (let i = 0; i < groupIndex; i++) {
                    globalIndex += groupedByTime[i].items.length;
                  }
                  globalIndex += indexInGroup;
                  
                  const index = globalIndex;
                  const arr = displayedItems;
            const sourceIndex = items.indexOf(it);
            // stable uid for selection & keys: prefer persisted ids but fall back to meta/payload or positional temp id
            const uid = it?.id || it?.meta?.itineraryEntryId || it?.meta?.itinerary_entry_id || it?.payload?.id || `day${editor.selectedDayIndex}-item${index}`;
            // detect placeholder / empty card so we can hide edit button for placeholders
            const hasPlaceholderFlag = it?.meta?.isPlaceholder === true;
            const hasNoTitle = !it?.title || it.title.trim() === '' || it.title === '新景点' || it.title === '新條目' || it.title === '行程条目' || it.title === '条目';
            const hasNoCard = !it?.card || (it.card && Object.keys(it.card).length === 0);
            const isTransportLocal = it?.type === 'transport';
            const isEmptyLocal = !isTransportLocal && hasNoTitle && hasNoCard;
            const isPlaceholderLocal = hasPlaceholderFlag || isEmptyLocal;
            const isSelected = selectedCardForDrag === uid || (isPlaceholderLocal && isEditing);
            const isInlineEditingThis = inlineEditTarget && inlineEditTarget.dayIndex === editor.selectedDayIndex && (typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index) === inlineEditTarget.itemIndex;
            const isOverThis = dragOverIndex === index;
            const isDraggingThis = draggedItem?.id === it.id;
            
            console.log(`[卡片渲染] ${it?.title} index=${index} isDragging=${isDraggingThis} uid=${uid}`);
            
            return (
              <React.Fragment key={`${uid}-${cardMappingHash}-wrapper`}>
              <div
                key={`${uid}-${cardMappingHash}`}
                ref={(el) => {
                  if (el) {
                    itemRefsRef.current.set(index, el);
                    if (index === 0) {
                      requestAnimationFrame(measureHandPosition);
                    }
                  } else {
                    itemRefsRef.current.delete(index);
                  }
                }}
                onPointerDown={(e: React.PointerEvent) => {
                  handlePointerDown(e, it, index, uid);
                }}
                onPointerUp={handlePointerUpLocal}
                onPointerLeave={handlePointerUpLocal}
                onPointerCancel={handlePointerUpLocal}
                onPointerDownCapture={(e: any) => {
                  try {
                    if (!isEditing) return;
                    const target = e.target as HTMLElement;
                    const closestInteractive = target && (target.closest && target.closest('input, textarea, select, button, a, [data-select-ignore]'));
                    const cardId = uid;
                    console.debug('[TimelineList] onPointerDownCapture', { cardId, tag: target?.tagName, closestInteractive: !!closestInteractive, isEditing });
                    if (closestInteractive) return;
                    // mark that pointerdown selected this card so click won't immediately toggle it off
                    pointerDownSuppressRef.current = cardId;
                    // record whether this pointerdown will change selection
                    pointerDownSelectedChangedRef.current = selectedCardForDrag !== cardId;
                    // safety: clear the suppress and selector-changed flag after short timeout in case click doesn't fire
                    setTimeout(() => {
                      if (pointerDownSuppressRef.current === cardId) pointerDownSuppressRef.current = null;
                      pointerDownSelectedChangedRef.current = false;
                    }, 300);
                  } catch (err) {}
                }}
                onClickCapture={(e: any) => {
                  try {
                    // 1. 检查是否点击了交互元素（如按钮、输入框），如果是则放行
                    const target = e.target as HTMLElement;
                    const closestInteractive = target && (target.closest && target.closest('input, textarea, select, button, a, [data-select-ignore]'));
                    if (closestInteractive) {
                      console.log('[onClickCapture] 点击了交互元素，放行');
                      return;
                    }

                    // 2. 如果刚刚触发了长按，则拦截点击（防止打开详情页）
                    if (longPressTriggeredRef.current) {
                      console.log('[onClickCapture] 拦截点击，因为刚刚触发了长按');
                      e.stopPropagation();
                      e.preventDefault();
                      longPressTriggeredRef.current = false;
                      return;
                    }

                    // 3. 如果卡片已经被选中，点击它应该取消选中（拦截点击，防止打开详情页）
                    if (selectedCardForDrag === uid) {
                        console.log('[onClickCapture] 取消选中', uid);
                        setSelectedCardForDrag(null);
                        setTooltipVisible(prev => ({ ...prev, [`tooltip-${uid}`]: false }));
                        e.stopPropagation();
                        e.preventDefault();
                        return;
                    }
                    
                    // 4. 如果未选中且非长按，放行点击。
                    // 后续的 onClick (冒泡阶段) 或子组件的 onClick 将处理打开详情页。
                  } catch (err) {
                    console.error('[onClickCapture] error', err);
                  }
                }}
                onClick={(e: any) => {
                  try {
                    // swallow clicks that are part of a drag
                    if (wasDraggedRef.current) {
                      e.stopPropagation();
                      e.preventDefault();
                      wasDraggedRef.current = false;
                      return;
                    }
                    if (!isEditing) return;
                    
                    // 注意：由于我们在 onClickCapture 中处理了长按拦截和取消选中，
                    // 能到达这里的点击事件，通常是“未选中状态下的短按”。
                    // 但为了保险，这里再次检查（虽然理论上 capture 阶段已经拦截了）。
                    
                    const target = e.target as HTMLElement;
                    const closestInteractive = target && (target.closest && target.closest('input, textarea, select, button, a, [data-select-ignore]'));
                    if (closestInteractive) return;

                    const cardId = uid;
                    
                    // Double check: 如果还是选中状态（理论上 capture 应该处理了），则不打开详情
                    if (selectedCardForDrag === cardId) {
                        return;
                    }
                    
                    // 打开详情页
                    const idx = typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index;
                    const placeholderMeta = Boolean(it?.meta?.isPlaceholder || it?.meta?.placeholder || it?.meta?.is_placeholder);
                    
                    // For transport items, use inline edit if available
                    const isTransport = it?.type === 'transport';
                    if (isTransport && setInlineEditTarget) {
                      setInlineEditTarget({ dayIndex: editor.selectedDayIndex, itemIndex: idx, mode: 'content' });
                    } else {
                      // For other items, open POI details
                      setOpenPoi(it, idx, {
                        fromPlaceholder: placeholderMeta,
                      });
                    }
                  } catch (err) {}
                }}
                  style={{
                  backgroundColor: isOverThis ? 'rgba(255, 122, 0, 0.25)' : 'transparent',
                  borderRadius: isOverThis ? '12px' : '0px',
                  // Don't use opacity to indicate selection because child elements (icons) would inherit it and appear dimmed.
                  // Use boxShadow/border to indicate selection instead.
                  opacity: isDraggingThis ? 0.8 : 1,
                  transform: isDraggingThis ? `translateY(${dragOffsetY}px) scale(0.98)` : 'translateY(0px)',
                  transition: isDraggingThis ? 'none' : 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  position: 'relative',
                  zIndex: isDraggingThis ? 1000 : (isOverThis ? 500 : 1),
                  boxShadow: selectedCardForDrag === uid ? '0 10px 30px rgba(251,122,74,0.08)' : undefined,
                  border: selectedCardForDrag === uid && !isOverThis ? '1px solid rgba(251,122,74,0.16)' : (isOverThis ? '3px solid #FF7A00' : '2px solid transparent'),
                  touchAction: 'pan-y',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  margin: '4px 0',
                  padding: '4px 0',
                  display: 'flex',
                  alignItems: 'stretch',
                }}
              >
                {/* Selection overlay: keep card dimmed when selected but allow icons (with higher z-index) to remain clickable above it */}
                {selectedCardForDrag === uid && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%',
                      // use a light overlay so card content looks dimmed but icons remain visible above it
                      background: 'rgba(255,255,255,0.3)',
                      borderRadius: 8,
                      zIndex: 2000,
                      // temporarily allow pointer events to pass through so icons remain fully interactive
                      pointerEvents: 'none'
                    }}
                  />
                )}
                <div style={{ 
                  flex: 1, 
                  position: 'relative',
                }}>
                  <TimelineItem
                  it={it}
                  index={index}
                  arr={arr}
                  sourceIndex={sourceIndex}
                  isEditing={isEditing}
                  hideTimeColumn={false}
                  // indicate selection state so TimelineItem can conditionally show actions
                  isSelected={selectedCardForDrag === uid}
                  onSelect={() => {
                    if (!isEditing) return;
                    const cardId = uid;
                    if (selectedCardForDrag === cardId) {
                      setSelectedCardForDrag(null);
                      setTooltipVisible(prev => ({ ...prev, [`tooltip-${cardId}`]: false }));
                    } else {
                      // Do NOT select on click (wait for long press)
                      // setSelectedCardForDrag(cardId);
                      // setTooltipVisible(prev => ({ ...prev, [`tooltip-${cardId}`]: true }));
                    }
                  }}
                  editor={editor}
                  selectedDay={selectedDay}
                  expandedCards={expandedCards}
                  toggleExpand={(cardId: string) => toggleExpand(cardId)}
                  setOpenPoi={(payload: any) => {
                    const rawPayload = payload && typeof payload === 'object' ? (payload.payload ?? payload) : undefined;
                    const isFromPlaceholder = Boolean(
                      payload && (
                        payload._fromPlaceholder === true ||
                        (payload.payload && payload.payload._fromPlaceholder === true)
                      )
                    );

                    if (isEditing && !isFromPlaceholder) {
                      // console.log('[TimelineList] Blocked click in editing mode');
                      // return;
                    }

                    // 從 payload 中取得 itemIndex（如果有的話），否則使用 sourceIndex
                    const itemIndexFromPayload = payload && typeof payload.itemIndex === 'number' ? payload.itemIndex : sourceIndex;
                    setOpenPoi(it, itemIndexFromPayload, {
                      fromPlaceholder: isFromPlaceholder,
                      payloadOverride: isFromPlaceholder ? rawPayload : undefined,
                    });
                  }}
                  setInlineEditTarget={setInlineEditTarget}
                  inlineEditTarget={inlineEditTarget}
                  dayIndex={editor.selectedDayIndex}
                  setEditItemIndex={setEditItemIndex}
                  setEditForm={setEditForm}
                  setEditModalVisible={setEditModalVisible}
                  parseTimeToMinutes={parseTimeToMinutes}
                  getNowMinutes={getNowMinutes}
                  isDraggable={true}
                  supabaseClient={supabaseClient}
                  currentUserId={currentUserId}
                  canEdit={canEdit}
                  groupIdForLocal={groupIdForLocal}
                  memberProfiles={memberProfiles}
                  onSelectFromCardPicker={onSelectFromCardPicker}
                  onImageClick={onImageClick}
                  // only render the drag / edit / delete icons when this card is selected
                  renderDragHandle={!isInlineEditingThis && selectedCardForDrag === uid ? () => {
                    // 窄屏下图标尺寸为 28px，宽屏为 36px
                    const iconSize = isNarrow ? 28 : 36;
                    const iconGap = isNarrow ? 4 : 6;
                    
                    return (
                      <div style={{ 
                        position: 'absolute', 
                        left: 8, 
                        top: 8, 
                        display: 'flex',
                        alignItems: 'center',
                        gap: iconGap,
                        zIndex: 99999 
                      }} data-select-ignore="1">
                        {/* 拖拽手柄 - 点击选中卡片 */}
                        <div 
                          style={{ position: 'relative', display: 'inline-block' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const cardId = uid;
                            // 切换选中状态
                            if (selectedCardForDrag === cardId) {
                              setSelectedCardForDrag(null);
                              setTooltipVisible(prev => ({ ...prev, [`tooltip-${cardId}`]: false }));
                            } else {
                              setSelectedCardForDrag(cardId);
                              setTooltipVisible(prev => ({ ...prev, [`tooltip-${cardId}`]: true }));
                            }
                          }}
                          onMouseEnter={() => {
                            const key = `tooltip-${uid}`;
                            setTooltipVisible(prev => ({ ...prev, [key]: true }));
                          }}
                          onMouseLeave={() => {
                            const key = `tooltip-${uid}`;
                            // 只有在未选中时才隐藏 tooltip
                            if (selectedCardForDrag !== uid) {
                              setTooltipVisible(prev => ({ ...prev, [key]: false }));
                            }
                          }}
                        >
                          <div
                            onPointerDown={(e: React.PointerEvent) => {
                              handlePointerDown(e, it, index, uid);
                            }}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUpLocal}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(251, 122, 74, 0.15)';
                              (e.currentTarget as HTMLElement).style.transform = 'scale(1.15)';
                              (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(251, 122, 74, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                              (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                              (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.15)';
                            }}
                            style={{
                              width: `${iconSize}px`,
                              height: `${iconSize}px`,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '3px',
                              cursor: 'grab',
                              backgroundColor: selectedCardForDrag === uid ? 'rgba(251, 122, 74, 0.25)' : 'rgba(255, 255, 255, 0.95)',
                              borderRadius: '8px',
                              boxShadow: selectedCardForDrag === uid ? '0 8px 26px rgba(251, 122, 74, 0.18)' : '0 2px 12px rgba(0, 0, 0, 0.15)',
                              userSelect: 'none',
                              touchAction: 'none',
                              transition: 'all 0.2s ease',
                              border: selectedCardForDrag === uid ? '2px solid rgba(251, 122, 74, 0.6)' : '1px solid rgba(0, 0, 0, 0.08)',
                              zIndex: 100000,
                              filter: selectedCardForDrag === uid ? 'brightness(1.06) drop-shadow(0 8px 20px rgba(251,122,74,0.12))' : undefined,
                              transform: selectedCardForDrag === uid ? 'scale(1.03)' : undefined
                            }}
                            data-select-ignore="1"
                          >
                            <div style={{ width: isNarrow ? '16px' : '18px', height: '3px', backgroundColor: selectedCardForDrag === uid ? '#FB7A4A' : '#555', borderRadius: '2px' }} />
                            <div style={{ width: isNarrow ? '16px' : '18px', height: '3px', backgroundColor: selectedCardForDrag === uid ? '#FB7A4A' : '#555', borderRadius: '2px' }} />
                            <div style={{ width: isNarrow ? '16px' : '18px', height: '3px', backgroundColor: selectedCardForDrag === uid ? '#FB7A4A' : '#555', borderRadius: '2px' }} />
                          </div>
                          {/* Tooltip - 显示在右下方 */}
                          {tooltipVisible[`tooltip-${uid}`] && (
                            <div
                              style={{
                                  position: 'absolute',
                                  left: 0,
                                  transform: 'translateX(-4px)',
                                  top: 'calc(100% + 8px)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                  color: 'white',
                                  padding: '10px 14px',
                                  borderRadius: '10px',
                                  fontSize: '13px',
                                  whiteSpace: 'nowrap',
                                  zIndex: 120000,
                                  boxShadow: '0 6px 22px rgba(0,0,0,0.42)',
                                  fontWeight: '500',
                                  pointerEvents: 'none'
                                }}
                            >
                              长按图标可以拖拉交换卡片的位置
                              <div style={{
                                position: 'absolute',
                                left: `${iconSize / 2 - 4}px`,
                                top: -8,
                                width: 0,
                                height: 0,
                                borderLeft: '8px solid transparent',
                                borderRight: '8px solid transparent',
                                borderBottom: '8px solid rgba(0, 0, 0, 0.9)',
                                pointerEvents: 'none'
                              }} />
                            </div>
                          )}
                        </div>
                        
                        {/* 编辑按钮 */}
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            const idx = typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index;
                            const dayIdx = editor.selectedDayIndex;
                            console.log('[编辑按钮] 打开编辑器', { it, dayIndex: dayIdx, index: idx });
                            
                            // 交通卡片使用 inline edit
                            const isTransport = it?.type === 'transport';
                            if (isTransport && setInlineEditTarget) {
                              setInlineEditTarget({ dayIndex: dayIdx, itemIndex: idx, mode: 'content' });
                              return;
                            }

                            // 其他卡片打开全屏编辑器
                            if (setEditItemIndex) setEditItemIndex(idx);
                            if (setEditForm) setEditForm(it);
                            if (setEditModalVisible) {
                             
                              setEditModalVisible(true);
                              return;
                            }

                            // 降级方案：使用 setOpenPoi
                            try {
                              if (setOpenPoi) {
                                const idx2 = typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index;
                                const placeholderMeta = Boolean(it?.meta?.isPlaceholder || it?.meta?.placeholder || it?.meta?.is_placeholder);
                                setOpenPoi(it, idx2, {
                                  fromPlaceholder: placeholderMeta,
                                });
                              }
                            } catch (e) {
                              console.error('[编辑按钮] 打开编辑器失败', e);
                            }
                          }}
                          style={{
                            width: `${iconSize}px`,
                            height: `${iconSize}px`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            backgroundColor: selectedCardForDrag === uid ? 'rgba(59,130,246,0.12)' : 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '8px',
                            boxShadow: selectedCardForDrag === uid ? '0 6px 20px rgba(59,130,246,0.18)' : '0 2px 12px rgba(0, 0, 0, 0.15)',
                            transition: 'all 0.2s ease',
                            border: selectedCardForDrag === uid ? '2px solid rgba(59,130,246,0.22)' : '1px solid rgba(0, 0, 0, 0.08)',
                              zIndex: 100000,
                              // make icon appear sharper when selected by adding subtle drop shadow & brightness
                              filter: selectedCardForDrag === uid ? 'brightness(1.06) drop-shadow(0 8px 20px rgba(59,130,246,0.12))' : undefined,
                              transform: selectedCardForDrag === uid ? 'scale(1.03)' : undefined
                          }}
                          data-select-ignore="1"
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(59, 130, 246, 0.15)';
                            (e.currentTarget as HTMLElement).style.transform = 'scale(1.15)';
                            (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                            (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                            (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.15)';
                          }}
                        >
                          <span style={{ fontSize: isNarrow ? '16px' : '18px' }}>✏️</span>
                        </div>
                        
                        {/* 删除按钮 */}
        <div
                          onClick={(e) => {
                              e.stopPropagation();
          // Always delete by source index (index in the underlying day.items array)
          const idx = typeof sourceIndex === 'number' && sourceIndex >= 0 ? sourceIndex : index;
                              const dayIdx = editor.selectedDayIndex;
                              console.log('[删除按钮] 请求删除卡片（等待确认）', { dayIndex: dayIdx, index: idx });

                              const performDelete = async () => {
                                try {
                                  // 如果有 card_id 且有 supabase client，执行服务器端删除
                                  const cardId = it?.card_id || it?.cardId || it?.id;
                                  if (cardId && supabaseClient && canEdit) {
                                    try {
                                      const { error } = await supabaseClient
                                        .from('cards_cards')
                                        .delete()
                                        .eq('card_id', cardId);

                                      if (error) {
                                        console.error('[删除按钮] Supabase delete error', error);
                                      }
                                    } catch (e) {
                                      console.error('[删除按钮] server delete unexpected', e);
                                    }
                                  }

                                  // 本地删除
                                  if (editor && typeof editor.deleteItem === 'function') {
                                    editor.deleteItem(dayIdx, idx);
                                  }

                                  setDirty && setDirty(true);
                                  try { showSuccess && showSuccess('已删除该卡片'); } catch (er) {}
                                } catch (e) {
                                  console.error('[删除按钮] delete flow failed', e);
                                  try { showError && showError('删除失败'); } catch (er) {}
                                }
                              };

                              // show iOS-style confirm modal; persist sourceIndex to ensure correct deletion target
                              setConfirmDelete({ visible: true, sourceIndex: idx, item: it });
                            }}
                            style={{
                            width: `${iconSize}px`,
                            height: `${iconSize}px`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            backgroundColor: selectedCardForDrag === uid ? 'rgba(239,68,68,0.08)' : 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '8px',
                            boxShadow: selectedCardForDrag === uid ? '0 8px 26px rgba(239,68,68,0.14)' : '0 2px 12px rgba(0, 0, 0, 0.15)',
                            transition: 'all 0.2s ease',
                            border: selectedCardForDrag === uid ? '2px solid rgba(239,68,68,0.14)' : '1px solid rgba(0, 0, 0, 0.08)',
                            zIndex: 100000,
                            filter: selectedCardForDrag === uid ? 'brightness(1.06) drop-shadow(0 8px 20px rgba(239,68,68,0.12))' : undefined,
                            transform: selectedCardForDrag === uid ? 'scale(1.03)' : undefined
                          }}
                          data-select-ignore="1"
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(239, 68, 68, 0.15)';
                            (e.currentTarget as HTMLElement).style.transform = 'scale(1.15)';
                            (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(239, 68, 68, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                            (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                            (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.15)';
                          }}
                        >
                          <span style={{ fontSize: isNarrow ? '16px' : '18px' }}>🗑️</span>
                        </div>
                      </div>
                    );
                  } : undefined}
                />
                </div>

              {/* 添加二级笔记入口 - 所有团员都可以使用，无需开启编辑模式（編輯模式下隱藏） */}
              {!isEditing && (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', marginTop: 12, marginBottom: 32 }}>
                  {/* 左侧时间列占位 */}
                  <div style={{ width: 60 }} />
                  {/* 右侧内容列 */}
                  <div style={{ flex: 1, paddingLeft: 12, paddingRight: 20 }}>
                    <div
                      onClick={() => setOpenInsertIndex(index)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpenInsertIndex(index); }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 18px',
                        borderRadius: 28,
                        background: '#fff',
                        border: '1px solid rgba(230,230,230,1)',
                        boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
                        cursor: 'pointer',
                        color: '#9CA3AF',
                        fontSize: 14,
                      }}
                    >
                      <span style={{ fontSize: 16, opacity: 0.9 }}>✎</span>
                      <span style={{ color: '#9CA3AF' }}>记录心情、待办、小事项...</span>
                    </div>
                  </div>
                </div>
              )}

              {!isEditing && openInsertIndex === index ? (
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
                    <NoteEditor
                      initialText=""
                      saving={insertSaving}
                      onCancel={() => setOpenInsertIndex(null)}
                      cardId={
                        displayedItems[index]?.card?.id
                        || displayedItems[index]?.card_id
                        || displayedItems[index]?.id
                        || undefined
                      }
                      currentUserId={currentUserId}
                      currentItineraryId={groupIdForLocal || undefined}
                      gallery={displayedItems[index]?.card?.image_gallery || []}
                      onGalleryUpdate={handleGalleryUpdateFactory(index)}
                      onSave={(data) => handleSaveNote(index, data)}
                    />
                  </div>
                ) : null}
              </div>
              </React.Fragment>
            );
          })}
          </div>
        );
      })}
      </div>
    </div>
  );
  }

  // 非编辑模式，使用普通版本（基于原始排序）
  return (
    <>
      {/* 登录提示模态框 (iOS style) - 当匿名用户尝试发小记时显示 */}
      <ProfileModal
        visible={showLoginPrompt}
        title="需要登录"
        message="只有注册用户才可以发小记，立即注册开始记录你的旅途吧！"
        cancelText="取消"
        confirmText="注册"
        onCancel={() => setShowLoginPrompt(false)}
        onConfirm={() => {
          setShowLoginPrompt(false);
          // 获取当前页面 URL 作为 referer，登录后返回
          const currentUrl = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
          window.location.href = `/auth?referer=${encodeURIComponent(currentUrl)}`;
        }}
      />
    <ScrollView 
      style={styles.timelineScroll} 
      contentContainerStyle={styles.timelineContent} 
      showsVerticalScrollIndicator={false}
    >
      {groupedByTime.map((group, groupIndex) => {
        const isMultipleItems = group.items.length > 1;
        
        // 多個項目時：左邊固定時間列 + 右邊卡片輪播
        if (isMultipleItems) {
          // 為每個時間組創建一個當前索引的 state key
          const groupKey = `group-${groupIndex}-${group.time}`;
          
          return (
            <MultiCardCarousel
              key={groupKey}
              group={group}
              groupIndex={groupIndex}
              groupedByTime={groupedByTime}
              displayedItems={displayedItems}
              items={items}
              isNarrow={isNarrow}
              isEditing={isEditing}
              editor={editor}
              selectedDay={selectedDay}
              expandedCards={expandedCards}
              toggleExpand={toggleExpand}
              setOpenPoi={setOpenPoi}
              setInlineEditTarget={setInlineEditTarget}
              inlineEditTarget={inlineEditTarget}
              parseTimeToMinutes={parseTimeToMinutes}
              getNowMinutes={getNowMinutes}
              supabaseClient={supabaseClient}
              currentUserId={currentUserId}
              canEdit={canEdit}
              groupIdForLocal={groupIdForLocal}
              memberProfiles={memberProfiles}
              onSelectFromCardPicker={onSelectFromCardPicker}
              onImageClick={onImageClick}
              setOpenInsertIndex={setOpenInsertIndex}
              openInsertIndex={openInsertIndex}
              insertSaving={insertSaving}
              onSaveNote={handleSaveNote}
              onCancelNote={() => setOpenInsertIndex(null)}
              onGalleryUpdateFactory={handleGalleryUpdateFactory}
            />
          );
        }

        // 單個項目：保持原有的垂直佈局
        return (
          <div 
            key={`time-group-${groupIndex}-${group.time}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0px',
            }}
          >
            {group.items.map((it: any, indexInGroup: number) => {
              // 计算全局 index
              let globalIndex = 0;
              for (let i = 0; i < groupIndex; i++) {
                globalIndex += groupedByTime[i].items.length;
              }
              globalIndex += indexInGroup;
              
              const index = globalIndex;
              const arr = displayedItems;
              const sourceIndex = items.indexOf(it);
              const uid = it?.id || it?.meta?.itineraryEntryId || it?.meta?.itinerary_entry_id || it?.payload?.id || `day${editor.selectedDayIndex}-item${index}`;
              
              return (
                <div key={`${uid}-view`} style={{ position: 'relative', marginBottom: 12 }}>
                  <TimelineItem
                    it={it}
                    index={index}
                    arr={arr}
                    sourceIndex={sourceIndex}
                    isEditing={false}
                    hideTimeColumn={false}
                    isSelected={false}
                    onSelect={() => {}}
                    editor={editor}
                    selectedDay={selectedDay}
                    expandedCards={expandedCards}
                    toggleExpand={toggleExpand}
                    setOpenPoi={setOpenPoi}
                    setInlineEditTarget={setInlineEditTarget}
                    inlineEditTarget={inlineEditTarget}
                    dayIndex={editor.selectedDayIndex}
                    parseTimeToMinutes={parseTimeToMinutes}
                    getNowMinutes={getNowMinutes}
                    isDraggable={false}
                    supabaseClient={supabaseClient}
                    currentUserId={currentUserId}
                    canEdit={canEdit}
                    groupIdForLocal={groupIdForLocal}
                    memberProfiles={memberProfiles}
                    onSelectFromCardPicker={onSelectFromCardPicker}
                    onImageClick={onImageClick}
                  />
                  
                  {/* Note Editor for single item view */}
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', marginTop: 12, marginBottom: 32 }}>
                    <div style={{ width: 60 }} />
                    <div style={{ flex: 1, paddingLeft: 12, paddingRight: 20 }}>
                      <div
                        onClick={() => setOpenInsertIndex(index)}
                        role="button"
                        tabIndex={0}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '10px 18px',
                          borderRadius: 28,
                          background: '#fff',
                          border: '1px solid rgba(230,230,230,1)',
                          boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
                          cursor: 'pointer',
                          color: '#9CA3AF',
                          fontSize: 14,
                        }}
                      >
                        <span style={{ fontSize: 16, opacity: 0.9 }}>✎</span>
                        <span style={{ color: '#9CA3AF' }}>记录心情、待办、小事项...</span>
                      </div>
                    </div>
                  </div>

                  {openInsertIndex === index && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
                      <NoteEditor
                        initialText=""
                        saving={insertSaving}
                        onCancel={() => setOpenInsertIndex(null)}
                        cardId={
                          displayedItems[index]?.card?.id
                          || displayedItems[index]?.card_id
                          || displayedItems[index]?.id
                          || undefined
                        }
                        currentUserId={currentUserId}
                        currentItineraryId={groupIdForLocal || undefined}
                        gallery={displayedItems[index]?.card?.image_gallery || []}
                        onGalleryUpdate={handleGalleryUpdateFactory(index)}
                        onSave={(data) => handleSaveNote(index, data)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </ScrollView>
    </>
  );
};

export default TimelineList;